import { useState, type FormEvent } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useT } from '@/i18n/locale';
import { ApiError } from '@/api/client';
import { useCreateInstance } from '@/api/hooks';

export const Route = createFileRoute('/_authenticated/instances/new')({
  component: NewInstancePage,
});

// Spiegelt normalizeUrlHost() der Desktop-Seite (BILLING_PLAN.md §4.1) — hier nur als Live-
// Vorschau fürs Formular, nicht die Source of Truth. Die Identität ist immer der URL-Host (D17).
function previewNormalize(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return new URL(withProtocol).hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
  } catch {
    return trimmed.toLowerCase();
  }
}

function NewInstancePage() {
  const t = useT();
  const navigate = useNavigate();
  const createInstance = useCreateInstance();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const preview = previewNormalize(value);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setFormError(null);
    try {
      // Identität immer per URL-Host (D17); `urlLabel` trägt den frei wählbaren Anzeige-Namen.
      const created = await createInstance.mutateAsync({
        identType: 'url',
        identValue: preview,
        urlLabel: name.trim() || undefined,
      });
      await navigate({ to: '/instances/$id', params: { id: created.id } });
    } catch (error) {
      if (error instanceof ApiError && error.error === 'ident_taken') {
        setFieldError(t('instances.new.error.identTaken'));
      } else {
        setFormError(t('instances.new.error.generic'));
      }
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="text-xl font-semibold text-slate-900">{t('instances.new.title')}</h1>
      <p className="mt-1 text-sm text-slate-500">{t('instances.new.subtitle')}</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5 rounded-xl border border-slate-200 bg-white p-5">
        <div>
          <label htmlFor="instance-name" className="block text-sm font-medium text-slate-900">
            {t('instances.new.nameLabel')}
          </label>
          <input
            id="instance-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('instances.new.namePlaceholder')}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <p className="mt-1 text-xs text-slate-500">{t('instances.new.nameHint')}</p>
        </div>

        <div>
          <label htmlFor="instance-url" className="block text-sm font-medium text-slate-900">
            {t('instances.new.urlLabel')}
          </label>
          <input
            id="instance-url"
            type="text"
            required
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('instances.new.urlPlaceholder')}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <p className="mt-1 text-xs leading-relaxed text-slate-500">{t('instances.new.urlIdentHint')}</p>
        </div>

        {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}

        {preview && !fieldError && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {t('instances.new.normalizedPreview', { value: preview })}
          </p>
        )}

        {formError && <p className="text-sm text-red-600">{formError}</p>}

        <button
          type="submit"
          disabled={createInstance.isPending || !value.trim()}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {createInstance.isPending ? t('instances.new.submitting') : t('instances.new.submit')}
        </button>
      </form>
    </div>
  );
}
